import { Outlet, Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight } from 'lucide-react';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { Wordmark } from '@/components/common/Wordmark';
import { useAuthStore } from '@/stores/authStore';

export function PublicShell() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);

  return (
    <div className="flex min-h-screen flex-col bg-bg">
      <header className="sticky top-0 z-40 border-b border-border/60 bg-bg/80 backdrop-blur-xl">
        <div className="mx-auto flex h-[4.5rem] max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
          <Link to="/" className="shrink-0 transition-opacity duration-300 hover:opacity-80">
            <Wordmark />
          </Link>
          <nav className="hidden items-center gap-6 whitespace-nowrap text-[0.9rem] lg:flex xl:gap-8">
            <a href="/#how-it-works" className="link-underline text-muted hover:text-ink">
              {t('landing.navHow')}
            </a>
            <a href="/#features" className="link-underline text-muted hover:text-ink">
              {t('landing.navFeatures')}
            </a>
            <a href="/#app" className="link-underline text-muted hover:text-ink">
              {t('landing.navApp')}
            </a>
            <NavLink to="/pricing" className="link-underline text-muted hover:text-ink">
              {t('landing.navPricing')}
            </NavLink>
            <a href="/#faq" className="link-underline text-muted hover:text-ink">
              FAQ
            </a>
          </nav>
          <div className="flex shrink-0 items-center gap-2">
            <LanguageSwitcher />
            <ThemeToggle />
            {user ? (
              <Link
                to="/dashboard"
                className="hidden items-center gap-1.5 rounded-2xl bg-clay-600 px-5 py-2.5 text-sm font-medium text-white shadow-soft transition-all duration-300 ease-soft hover:-translate-y-0.5 hover:bg-clay-700 hover:shadow-glow sm:inline-flex"
              >
                {t('dashboard.title')} <ArrowUpRight size={15} />
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="hidden text-sm font-medium text-muted transition-colors hover:text-ink sm:inline"
                >
                  {t('common.login')}
                </Link>
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-1.5 rounded-2xl bg-clay-600 px-5 py-2.5 text-sm font-medium text-white shadow-soft transition-all duration-300 ease-soft hover:-translate-y-0.5 hover:bg-clay-700 hover:shadow-glow"
                >
                  {t('common.getStarted')} <ArrowUpRight size={15} />
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-border/60 bg-faint">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <div className="grid gap-12 sm:grid-cols-2 md:grid-cols-4">
            <div className="max-w-xs">
              <Wordmark />
              <p className="mt-4 text-sm leading-relaxed text-muted">
                {t('landing.footerTagline')}
              </p>
            </div>
            <FooterCol title={t('landing.product')}>
              <FooterLink to="/pricing">{t('billing.choosePlan')}</FooterLink>
              <FooterLink href="/#features">{t('landing.navFeatures')}</FooterLink>
              <FooterLink href="/#how-it-works">{t('landing.navHow')}</FooterLink>
            </FooterCol>
            <FooterCol title={t('landing.company')}>
              <FooterLink href="mailto:support@ministores.shop">support@ministores.shop</FooterLink>
            </FooterCol>
            <FooterCol title={t('common.appName')}>
              <li className="text-sm text-muted">{t('landing.trustLine')}</li>
              <li className="text-sm text-muted">© {new Date().getFullYear()} MiniShop</li>
            </FooterCol>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FooterCol({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="eyebrow">{title}</p>
      <ul className="mt-4 space-y-2.5">{children}</ul>
    </div>
  );
}

function FooterLink({
  to,
  href,
  children,
}: {
  to?: string;
  href?: string;
  children: React.ReactNode;
}) {
  const cls = 'text-sm text-ink/80 transition-colors duration-300 hover:text-primary';
  return (
    <li>
      {to ? (
        <Link to={to} className={cls}>
          {children}
        </Link>
      ) : (
        <a href={href} className={cls}>
          {children}
        </a>
      )}
    </li>
  );
}
